import CheckCircle from '@mui/icons-material/CheckCircle'
import { Box } from '@mui/material'
import { getGridStringOperators, GridColDef, GridRenderCellParams, GridValueGetter } from '@mui/x-data-grid-premium'
import { customNotContainsOperator } from 'components/DataGrid/operators/customNotContainsOperator'
import {
  customBooleanSortOperator,
  customDateSortOperator,
  customNumericSortOperator,
  customSortOperator,
} from 'components/DataGrid/operators/customSortOperator'
import { getDensityStyles } from 'components/DataGrid/operators/getDensityStyles'
import AggregateCurrencyCell from 'components/MTB/grid/cells/AggregateCell'
import { EntityAvatar } from 'theme/components/Avatar'
import { OrganizationTableItem } from '../../../types'
import { formatDateString, formatDateTime } from '../../../utils/formatDayjsDateTime'
import TeamMemberCell from '../../Organizations/grid/cells/TeamMemberCell'
import { renderChannels } from './cells/renderChannels'
import RenderContractCell from './cells/RenderContractCell'
import RenderContractedByCell from './cells/RenderContractedByCell'
import RenderCountCell from './cells/RenderCountCell'
import RenderDateCell from './cells/RenderDateCell'
import RenderDealNameCell from './cells/RenderDealNameCell'
import RenderDealOrganizationCell from './cells/RenderDealOrganizationCell'
import RenderDealStatusCell from './cells/RenderDealStatusCell'
import RenderDealTypeCell from './cells/RenderDealTypeCell'
import RenderProbabilityCell from './cells/RenderProbabilityCell'
import RenderSeoTierCell from './cells/RenderSeoTierCell'
import RenderStatusCell from './cells/RenderStatusCell'
const TERMINAL_STATUSES = ['Ended', 'Cancelled', 'Replaced', 'Canceled']

export const useDealGridColumns = () => {
  const dealGridColumns = (
    deals: OrganizationTableItem[],
    density: 'compact' | 'standard' | 'comfortable',
    navigate: (route: string) => void
    // isExpanded: (id: GridRowId) => boolean
  ): GridColDef[] => {
    const _route = ''
    const densityStyles = getDensityStyles(density)

    const columns: GridColDef[] = [
      {
        field: 'deal_uuid',
        headerName: 'DEAL_UUID',
        aggregable: false,
        width: 100,
        headerAlign: 'left',
        filterOperators: [...getGridStringOperators(), customNotContainsOperator],
        sortComparator: customSortOperator,
        valueGetter: (_value, row) => row?.deal_uuid,
      },
      {
        field: 'predecessorId',
        headerName: 'Predecessor Id',
        aggregable: false,
        width: 100,
        headerAlign: 'left',
        filterOperators: [...getGridStringOperators(), customNotContainsOperator],
        sortComparator: customSortOperator,
        valueGetter: (_value, row) => row?.predecessorId ?? null,
      },
      {
        field: 'successorId',
        headerName: 'Successor Id',
        aggregable: false,
        width: 100,
        headerAlign: 'left',
        filterOperators: [...getGridStringOperators(), customNotContainsOperator],
        sortComparator: customSortOperator,
        valueGetter: (_value, row) => row?.successorId ?? null,
      },
      {
        field: 'source',
        headerName: 'Source',
        width: 50,
        filterOperators: [...getGridStringOperators(), customNotContainsOperator],
        sortComparator: customSortOperator,
        valueGetter: (_value, row) => row?.source ?? null,
      },
      {
        field: 'name',
        headerName: 'Deal Name',
        aggregable: false,
        minWidth: 200,
        flex: 1,
        headerAlign: 'left',
        filterOperators: [...getGridStringOperators(), customNotContainsOperator],
        valueFormatter: (_, row) => {
          const org = deals?.find(d => d?.SK === row?.SK)
          const value = row?.isParent ? row?.name : `${org?.name}-${row?.name}`
          return value
        },
        sortComparator: customSortOperator,
        valueGetter: (_, row) => row?.name,
        renderCell: (params: GridRenderCellParams) => (
          <RenderDealNameCell params={params} navigate={navigate} densityStyles={densityStyles} />
        ),
      },

      {
        field: 'generated_with',
        headerName: 'Generated With',
        aggregable: false,
        width: 100,
        headerAlign: 'center',
        filterOperators: [...getGridStringOperators(), customNotContainsOperator],
        sortComparator: customSortOperator,
        valueGetter: (params: GridValueGetter) => params,
      },
      {
        field: 'semanticVersion',
        headerName: 'Version',
        aggregable: false,
        width: 100,
        headerAlign: 'center',
        filterOperators: [...getGridStringOperators(), customNotContainsOperator],
        sortComparator: customSortOperator,
        valueGetter: (params: GridValueGetter) => params,
      },
      {
        field: 'masterAgreementId',
        headerName: 'M. Deal Id',
        aggregable: false,
        width: 100,
        headerAlign: 'center',
        filterOperators: [...getGridStringOperators(), customNotContainsOperator],
        sortComparator: customSortOperator,
        valueGetter: (params: GridValueGetter) => params,
      },
      {
        field: 'dealStatus',
        headerName: 'Deal Status',
        aggregable: false,
        width: 100,
        headerAlign: 'center',
        filterOperators: [...getGridStringOperators(), customNotContainsOperator],
        sortComparator: customSortOperator,
        valueGetter: (params: GridValueGetter) => params,
        renderCell: (params: GridRenderCellParams) => <RenderDealStatusCell params={params} />,
      },
      {
        field: 'oldStatus',
        headerName: 'Old Status',
        aggregable: false,
        width: 100,
        headerAlign: 'center',
        filterOperators: [...getGridStringOperators(), customNotContainsOperator],
        sortComparator: customSortOperator,
        valueGetter: (_, row) => row?.oldStatus,
        renderCell: (params: GridRenderCellParams) => <RenderDealStatusCell params={params} />,
      },
      {
        field: 'monthlyTotal',
        headerName: 'Mo. Total',
        aggregable: true,
        type: 'number',
        width: 125,
        headerAlign: 'center',
        filterOperators: [...getGridStringOperators(), customNotContainsOperator],
        sortComparator: customNumericSortOperator,
        valueGetter: (_, row) => {
          return parseFloat(row?.totals?.monthlyTotal || row?.monthlyTotal || '0')
        },
        // valueGetter: (_, row) => parseFloat(row?.monthlyTotal || row?.totals?.monthlyTotal || '0'),
        renderCell: (params: GridRenderCellParams) => (
          <AggregateCurrencyCell params={params} densityStyles={densityStyles} />
        ),
      },
      {
        field: 'total',
        headerName: 'Total',
        aggregable: true,
        type: 'number',
        width: 100,
        headerAlign: 'center',
        filterOperators: [...getGridStringOperators(), customNotContainsOperator],
        sortComparator: customNumericSortOperator,
        valueGetter: (_, row) => {
          const isCsa = ['CSA', 'RCSA'].includes(row?.type)
          return isCsa
            ? parseFloat((row?.totals?.monthlyTotal || row?.contractTotal) ?? '0')
            : parseFloat((row?.totals?.total || row?.contractTotal) ?? '0')
        },
        renderCell: (params: GridRenderCellParams) => (
          <AggregateCurrencyCell params={params} densityStyles={densityStyles} />
        ),
      },
      {
        field: 'type',
        headerName: 'Type',
        aggregable: false,
        width: 100,
        headerAlign: 'center',
        filterOperators: [...getGridStringOperators(), customNotContainsOperator],
        sortComparator: customSortOperator,
        valueGetter: (params: GridValueGetter) => params,
        renderCell: (params: GridRenderCellParams) => (
          <RenderDealTypeCell params={params} densityStyles={densityStyles} />
        ),
      },
      {
        field: 'seoTier',
        headerName: 'SEO',
        aggregable: false,
        width: 75,
        headerAlign: 'center',
        filterOperators: [...getGridStringOperators(), customNotContainsOperator],
        sortComparator: customSortOperator,
        valueGetter: (_value, row): GridValueGetter => row?.seoTier?.label ?? null,
        renderCell: (params: GridRenderCellParams) => {
          //@ts-ignore
          if (params?.rowNode?.isAutoGenerated) return
          return <RenderSeoTierCell params={params} densityStyles={densityStyles} />
        },
      },
      {
        field: 'startDate',
        headerName: 'Start Date',
        aggregable: false,
        width: 100,
        headerAlign: 'center',
        filterOperators: [...getGridStringOperators(), customNotContainsOperator],
        sortComparator: customDateSortOperator,
        valueGetter: params => {
          return params ? formatDateString(params) : null
        },
        renderCell: (params: GridRenderCellParams) => <RenderDateCell params={params} densityStyles={densityStyles} />,
      },
      {
        field: 'endDate',
        headerName: 'End Date',
        aggregable: false,
        width: 100,
        headerAlign: 'center',
        filterOperators: [...getGridStringOperators(), customNotContainsOperator],
        sortComparator: customDateSortOperator,
        valueGetter: params => {
          return params ? formatDateString(params) : null
        },
        renderCell: (params: GridRenderCellParams) => <RenderDateCell params={params} densityStyles={densityStyles} />,
      },
      {
        field: 'contractId',
        headerName: 'Contract',
        aggregable: false,
        width: 100,
        headerAlign: 'center',
        filterOperators: [...getGridStringOperators(), customNotContainsOperator],
        sortComparator: customSortOperator,
        valueGetter: params => {
          return params ?? null
        },
        renderCell: (params: GridRenderCellParams) => {
          //@ts-ignore
          if (params?.rowNode?.isAutoGenerated) return
          return <RenderContractCell params={params} densityStyles={densityStyles} />
        },
      },
      {
        field: 'noSignature',
        headerName: 'Internal?',
        aggregable: false,
        width: 100,
        headerAlign: 'center',
        filterOperators: [...getGridStringOperators(), customNotContainsOperator],
        sortComparator: customBooleanSortOperator,
        valueGetter: (_, row) => {
          return row?.noSignature
        },
        renderCell: (params: GridRenderCellParams) => {
          //@ts-ignore
          if (params?.rowNode?.isAutoGenerated) return
          const isNoSignature = params?.value
          return isNoSignature ? (
            <CheckCircle sx={{ color: 'text.success' }} />
          ) : (
            <CheckCircle sx={{ color: 'text.disabled' }} />
          )
        },
      },
      {
        field: 'organization',
        headerName: 'Organization',
        aggregable: false,
        minWidth: 175,
        flex: 0.7,
        headerAlign: 'left',
        filterOperators: [...getGridStringOperators(), customNotContainsOperator],
        sortComparator: customSortOperator,
        valueGetter: (_value, row): GridValueGetter => {
          return row?.organization?.name ?? ''
        },
        renderCell: (params: GridRenderCellParams) => {
          //@ts-ignore
          if (params?.rowNode?.isAutoGenerated) return
          return <RenderDealOrganizationCell params={params} densityStyles={densityStyles} navigate={navigate} />
        },
      },
      {
        field: 'daysLeft',
        headerName: 'Days Left',
        aggregable: false,
        width: 50,
        headerAlign: 'left',
        filterOperators: [...getGridStringOperators(), customNotContainsOperator],
        sortComparator: customNumericSortOperator,
        valueGetter: (_, row) => {
          const value = row?.daysLeft && row?.daysLeft >= 0 ? row?.daysLeft : 0
          // If it's a number, return it
          if (typeof value === 'number') return value
          // If it's a string that can be converted to a number
          if (typeof value === 'string' && !isNaN(Number(value))) return Number(value)
          // Else fallback
          return 0
        },
        renderCell: params => {
          //@ts-ignore
          if (params?.rowNode?.isAutoGenerated) return null
          return <RenderCountCell params={params} densityStyles={densityStyles} />
        },
      },
      {
        field: 'contractedBy',
        headerName: 'Contracted By',
        aggregable: false,
        minWidth: 150,
        flex: 0.6,
        headerAlign: 'left',
        filterOperators: [...getGridStringOperators(), customNotContainsOperator],
        sortComparator: customSortOperator,
        valueGetter: (_value, row) => {
          // Get from resolved contractedBy (from organization's contractedBy)
          const resolved = row?._resolvedContractedBy
          if (Array.isArray(resolved) && resolved.length > 0) {
            return resolved.map((o: any) => o?.name).join(', ')
          }
          return row?.contractedBy?.name || ''
        },
        renderCell: params => {
          //@ts-ignore
          if (params?.rowNode?.isAutoGenerated) return
          return <RenderContractedByCell params={params} densityStyles={densityStyles} navigate={navigate} />
        },
      },
      {
        field: 'status',
        headerName: 'Document Status',
        aggregable: false,
        width: 100,
        headerAlign: 'center',
        filterOperators: [...getGridStringOperators(), customNotContainsOperator],
        sortComparator: customSortOperator,
        valueGetter: (params: GridValueGetter) => params,
        renderCell: (params: GridRenderCellParams) => (
          <RenderStatusCell params={params} densityStyles={densityStyles} />
        ),
      },
      {
        field: 'stage',
        headerName: 'Deal Stage',
        aggregable: false,
        width: 100,
        headerAlign: 'center',
        filterOperators: [...getGridStringOperators(), customNotContainsOperator],
        sortComparator: customSortOperator,
        valueGetter: params => params,
        renderCell: params => <RenderStatusCell params={params} densityStyles={densityStyles} />,
      },
      {
        field: 'lastDateOfService',
        headerName: 'Last Date of Service',
        aggregable: false,
        width: 100,
        headerAlign: 'left',
        filterOperators: [...getGridStringOperators(), customNotContainsOperator],
        sortComparator: customDateSortOperator,
        valueGetter: params => {
          return params ? formatDateString(params) : null
        },
        renderCell: (params: GridRenderCellParams) => <RenderDateCell params={params} densityStyles={densityStyles} />,
      },
      {
        field: 'lastModified',
        headerName: 'Modified',
        aggregable: false,
        width: 100,
        headerAlign: 'left',
        filterOperators: [...getGridStringOperators(), customNotContainsOperator],
        sortComparator: customDateSortOperator,
        valueGetter: params => {
          return params ? formatDateString(params) : null
        },
        renderCell: (params: GridRenderCellParams) => <RenderDateCell params={params} densityStyles={densityStyles} />,
      },
      {
        field: 'timeCompleted',
        headerName: 'Date Won',
        aggregable: false,
        width: 100,
        headerAlign: 'left',
        filterOperators: [...getGridStringOperators(), customNotContainsOperator],
        sortComparator: customDateSortOperator,
        valueGetter: (_value, row) => {
          return row?.timeCompleted ? formatDateTime(row?.timeCompleted) : null
        },
        renderCell: (params: GridRenderCellParams) => <RenderDateCell params={params} densityStyles={densityStyles} />,
      },
      {
        field: 'owner',
        headerName: 'Owner',
        aggregable: false,
        width: 75,
        headerAlign: 'center',
        filterOperators: [...getGridStringOperators(), customNotContainsOperator],
        sortComparator: customSortOperator,
        valueGetter: (_value, row) => {
          return row?._resolvedOwner?.name || row?.owner?.name || ''
        },
        renderCell: params => {
          // @ts-ignore
          if (params?.rowNode?.isAutoGenerated) return
          const { _resolvedOwner } = params?.row ?? null
          return (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100%',
                width: '100%',
              }}
            >
              <EntityAvatar
                name={_resolvedOwner?.name}
                photoUrl={_resolvedOwner?.image_512}
                userId={_resolvedOwner?.user_uuid}
                size={densityStyles.size}
                tooltip
                enableNav
              />
            </Box>
          )
        },
      },
      // Team role columns (from organization's team)
      {
        field: 'AM',
        headerName: 'AM',
        aggregable: false,
        width: 75,
        headerAlign: 'center',
        filterOperators: [...getGridStringOperators(), customNotContainsOperator],
        sortComparator: customSortOperator,
        valueGetter: (_value, row) => {
          const team = row?._resolvedTeam?.AM || []
          return team.map((u: any) => u?.name).join(', ')
        },
        renderCell: params => <TeamMemberCell params={params} densityStyles={densityStyles} />,
      },
      {
        field: 'PM',
        headerName: 'PM',
        aggregable: false,
        width: 75,
        headerAlign: 'center',
        filterOperators: [...getGridStringOperators(), customNotContainsOperator],
        sortComparator: customSortOperator,
        valueGetter: (_value, row) => {
          const team = row?._resolvedTeam?.PM || []
          return team.map((u: any) => u?.name).join(', ')
        },
        renderCell: params => <TeamMemberCell params={params} densityStyles={densityStyles} />,
      },
      {
        field: 'DMS',
        headerName: 'DMS',
        aggregable: false,
        width: 75,
        headerAlign: 'center',
        filterOperators: [...getGridStringOperators(), customNotContainsOperator],
        sortComparator: customSortOperator,
        valueGetter: (_value, row) => {
          const team = row?._resolvedTeam?.DMS || []
          return team.map((u: any) => u?.name).join(', ')
        },
        renderCell: params => <TeamMemberCell params={params} densityStyles={densityStyles} />,
      },
      {
        field: 'SEO',
        headerName: 'SEO',
        aggregable: false,
        width: 75,
        headerAlign: 'center',
        filterOperators: [...getGridStringOperators(), customNotContainsOperator],
        sortComparator: customSortOperator,
        valueGetter: (_value, row) => {
          const team = row?._resolvedTeam?.SEO || []
          return team.map((u: any) => u?.name).join(', ')
        },
        renderCell: params => <TeamMemberCell params={params} densityStyles={densityStyles} />,
      },
      {
        field: 'MIA',
        headerName: 'MIA',
        aggregable: false,
        width: 75,
        headerAlign: 'center',
        filterOperators: [...getGridStringOperators(), customNotContainsOperator],
        sortComparator: customSortOperator,
        valueGetter: (_value, row) => {
          const team = row?._resolvedTeam?.MIA || []
          return team.map((u: any) => u?.name).join(', ')
        },
        renderCell: params => <TeamMemberCell params={params} densityStyles={densityStyles} />,
      },
      {
        field: 'BDE',
        headerName: 'BDE',
        aggregable: false,
        width: 75,
        headerAlign: 'center',
        filterOperators: [...getGridStringOperators(), customNotContainsOperator],
        sortComparator: customSortOperator,
        valueGetter: (_value, row) => {
          const team = row?._resolvedTeam?.BDE || []
          return team.map((u: any) => u?.name).join(', ')
        },
        renderCell: params => <TeamMemberCell params={params} densityStyles={densityStyles} />,
      },
      {
        field: 'QA',
        headerName: 'QA',
        aggregable: false,
        width: 75,
        headerAlign: 'center',
        filterOperators: [...getGridStringOperators(), customNotContainsOperator],
        sortComparator: customSortOperator,
        valueGetter: (_value, row) => {
          const team = row?._resolvedTeam?.QA || []
          return team.map((u: any) => u?.name).join(', ')
        },
        renderCell: params => <TeamMemberCell params={params} densityStyles={densityStyles} />,
      },
      {
        field: 'implementationFee',
        headerName: 'Impl. Fee',
        aggregable: true,
        type: 'number',
        width: 100,
        headerAlign: 'center',
        filterOperators: [...getGridStringOperators(), customNotContainsOperator],
        sortComparator: customNumericSortOperator,
        valueFormatter: params => {
          return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 2,
          }).format(params)
        },
        valueGetter: (_, row) => {
          if (row?.implementationFee === '') return 0
          return row?.implementationFee ?? 0
        },
        renderCell: params => <AggregateCurrencyCell params={params} densityStyles={densityStyles} />,
      },
      {
        field: 'creation_timestamp',
        headerName: 'Created Time',
        aggregable: false,
        width: 100,
        headerAlign: 'center',
        filterOperators: [...getGridStringOperators(), customNotContainsOperator],
        sortComparator: customDateSortOperator,
        valueGetter: params => {
          return params ? formatDateTime(params) : null
        },
        renderCell: (params: GridRenderCellParams) => <RenderDateCell params={params} densityStyles={densityStyles} />,
      },
      {
        field: 'probability',
        headerName: 'Probability',
        aggregable: true,
        type: 'number',
        width: 100,
        headerAlign: 'center',
        filterOperators: [...getGridStringOperators(), customNotContainsOperator],
        sortComparator: customNumericSortOperator,
        valueFormatter: params => {
          const formatted = params && typeof params != 'number' ? parseFloat(params) : 0
          return formatted
        },
        valueGetter: (_, row) => {
          const value = row?.probability ? parseFloat(row?.probability) : 0
          return Math.round(value)
        },
        renderCell: (params: GridRenderCellParams) => {
          return <RenderProbabilityCell params={params} densityStyles={densityStyles} />
        },
      },
      {
        field: 'weightedTotal',
        headerName: 'Total (wtd.)',
        aggregable: true,
        type: 'number',
        width: 100,
        headerAlign: 'center',
        filterOperators: [...getGridStringOperators(), customNotContainsOperator],
        sortComparator: customNumericSortOperator,
        valueFormatter: params => {
          return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 2,
          }).format(params)
        },
        valueGetter: (_, row) => {
          return row?.weightedTotal ? parseFloat(row?.weightedTotal) : 0
        },
        renderCell: (params: GridRenderCellParams) => (
          <AggregateCurrencyCell params={params} densityStyles={densityStyles} />
        ),
      },
      ...renderChannels(densityStyles),
    ]
    return columns
  }
  return { dealGridColumns }
}
